import React, {
  useState,
  useEffect,
  useMemo,
  memo,
  useRef,
  useId,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  GraduationCap,
  Users as UsersIcon,
  BookOpen,
  DollarSign,
  AlertTriangle,
  ListFilter,
  Columns3,
  Ellipsis,
  ChevronDown,
  ChevronUp,
  X,
  CheckCircle,
  XCircle,
  CircleX,
  Loader2,
  IndianRupee,
  Calculator,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type Row,
} from "@tanstack/react-table";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  courseGroupService,
  CourseGroup,
  CreateCourseGroupData,
  UpdateCourseGroupData,
  UpdateCourseGroupWithYearwiseFeesData,
  YearWiseFee,
} from "@/services/courseGroupService";
import { Loader } from "../ui/loader";
import { RootState } from "@/store";
import CreateCourseGroupDialog from "@/components/staff/CreateCourseGroupDialog";

// Wrapper component for the create course group dialog with trigger button
const CreateCourseGroupWrapper: React.FC<{ onCourseGroupCreated: () => void }> = ({ onCourseGroupCreated }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button size="icon" onClick={() => setIsOpen(true)}>
        <Plus className="h-4 w-4" />
      </Button>
      <CreateCourseGroupDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onCourseGroupCreated={() => {
          onCourseGroupCreated();
          setIsOpen(false);
        }}
      />
    </>
  );
};

// Form validation schemas
const updateGroupSchema = z.object({
  name: z.string().min(1, "Group name is required"),
  code: z.string().min(1, "Group code is required"),
  description: z.string().optional(),
});

const updateGroupWithFeesSchema = z.object({
  name: z.string().min(1, "Group name is required"),
  code: z.string().min(1, "Group code is required"),
  description: z.string().optional(),
  yearWiseFees: z.array(z.object({
    yearNumber: z.number(),
    feeAmount: z.number().min(0, "Fee amount must be non-negative"),
  })).optional(),
});

type UpdateGroupFormData = z.infer<typeof updateGroupSchema>;
type UpdateGroupWithFeesFormData = z.infer<typeof updateGroupWithFeesSchema>;

// Memoized search input component to prevent focus loss
interface SearchInputProps {
  id: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  searchInProgress: boolean;
}

const SearchInput = memo<SearchInputProps>(
  ({ id, searchTerm, onSearchChange, onSearchClear, searchInProgress }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <div className="relative bg-white flex-1">
        <Input
          id={`${id}-input`}
          ref={inputRef}
          className={cn("ps-9", Boolean(searchTerm) && "pe-9")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter by name, code, or description..."
          type="text"
          aria-label="Filter by name, code, or description"
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
          {searchInProgress ? (
            <Loader2 className="animate-spin h-4 w-4" />
          ) : (
            <Search className="stroke-2 h-4 w-4" aria-hidden="true" />
          )}
        </div>
        {Boolean(searchTerm) && (
          <button
            className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Clear filter"
            onClick={onSearchClear}
          >
            <CircleX className="stroke-2 h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);

// Row actions component
interface RowActionsProps {
  row: Row<CourseGroup>;
  onEdit: (group: CourseGroup) => void;
  onDelete: (group: CourseGroup) => void;
  hasFullAccess?: boolean;
}

function RowActions({
  row,
  onEdit,
  onDelete,
  hasFullAccess = true,
}: RowActionsProps) {
  const group = row.original;

  if (!hasFullAccess) {
    // For STAFF users (read-only), show no actions or a simple view indicator
    return (
      <div className="flex justify-center">
        <span className="text-xs text-gray-400">View Only</span>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Edit item"
          >
            <Ellipsis className="stroke-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => onEdit(group)}>
              Edit
            </DropdownMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                  disabled={group.studentCount > 0}
                >
                  Delete
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the course group "{group.name} ({group.code})".
                    {group.studentCount > 0 && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                        This group has {group.studentCount} enrolled students
                        and cannot be deleted.
                      </div>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(group)}
                    disabled={group.studentCount > 0}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Table columns definition

const CollegeGroups = () => {
  const id = useId();
  // Get current user from auth state to determine permissions
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const isStaff = currentUser?.role === "STAFF";
  const hasFullAccess = !isStaff; // COLLEGE_ADMIN and other roles have full access
  const columns: ColumnDef<CourseGroup>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }: { row: Row<CourseGroup> }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-0.5"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: "Group Details",
      cell: ({ row }: { row: Row<CourseGroup> }) => {
        const group = row.original;
        return (
          <div>
            <div className="font-semibold text-gray-900">{group.name}</div>
            <div className="flex items-center space-x-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {group.code}
              </Badge>
            </div>
            {group.description && (
              <div className="text-sm text-gray-500 mt-1 max-w-xs truncate">
                {group.description}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "durationYears",
      header: "Program Info",
      cell: ({ row }: { row: Row<CourseGroup> }) => {
        const group = row.original;
        return <span className="text-sm">{group.durationYears} Years</span>;
      },
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }: { row: Row<CourseGroup> }) => {
        const isActive = row.original.isActive;
        return (
          <Badge
            className={cn(
              !isActive && "bg-muted-foreground/60 text-primary-foreground"
            )}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "totalFee",
      header: "Fee Structure",
      cell: ({ row }: { row: Row<CourseGroup> }) => {
        const group = row.original;
        return <span className="text-sm">{group.formattedFee}</span>;
      },
    },
    {
      accessorKey: "studentCount",
      header: "Students",
      cell: ({ row }: { row: Row<CourseGroup> }) => {
        const group = row.original;
        return <span className="text-sm">{group.studentCount}</span>;
      },
    },
    ...(hasFullAccess
    ? [
        {
          id: "actions",
          enableHiding: false,
          cell: ({ row }: { row: Row<CourseGroup> }) => (
            <RowActions
              row={row}
              onEdit={(group) => {
                // handled by the component
              }}
              onDelete={(group) => {
                // handled by the component
              }}
            />
          ),
        },
      ]
    : []),
  ];

  

  const [groups, setGroups] = useState<CourseGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInProgress, setSearchInProgress] = useState(false);

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CourseGroup | null>(null);
  const [editingYearWiseFees, setEditingYearWiseFees] = useState<YearWiseFee[]>([]);
  const [canEditFees, setCanEditFees] = useState(true); // Allow fee editing by default

  // Form instances - Keep only edit form
  const editForm = useForm<UpdateGroupWithFeesFormData>({
    resolver: zodResolver(updateGroupWithFeesSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
    },
  });

  // Filtered data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm) return groups;
    return groups.filter(
      (group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description &&
          group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [groups, searchTerm]);

  // Update the columns to use the handlers and conditionally include selection
  const columnsWithHandlers = useMemo(() => {
    const baseColumns = columns
      .filter((column) => {
        // Remove select column for STAFF users (read-only access)
        if (column.id === "select" && isStaff) {
          return false;
        }
        return true;
      })
      .map((column) => {
        if (column.id === "actions") {
          return {
            ...column,
            cell: ({ row }: { row: Row<CourseGroup> }) => (
              <RowActions
                row={row}
                onEdit={hasFullAccess ? handleEditGroup : () => {}} // Disable edit for STAFF
                onDelete={hasFullAccess ? handleDeleteGroup : () => {}} // Disable delete for STAFF
                hasFullAccess={hasFullAccess} // Pass permission flag to component
              />
            ),
          };
        }
        return column;
      });
    return baseColumns;
  }, [isStaff, hasFullAccess]);

  // Table instance
  const table = useReactTable({
    data: filteredData,
    columns: columnsWithHandlers,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Load groups
  const loadGroups = async () => {
    try {
      setIsLoading(true);
      const response = await courseGroupService.getAllCourseGroups({
        page: 1,
        limit: 100, // Load all groups for client-side pagination
        sortBy: "createdAt",
        sortOrder: "DESC",
      });

      setGroups(response.data);
    } catch (error) {
      console.error("Error loading groups:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  // Create group handler
  // Edit group handler
  const handleEditGroup = (group: CourseGroup) => {
    setEditingGroup(group);
    setCanEditFees(true); // Allow fee editing for all groups
    
    // Initialize year-wise fees
    if (group.yearWiseFees && group.yearWiseFees.length > 0) {
      setEditingYearWiseFees([...group.yearWiseFees]);
    } else {
      // Create default year-wise fees for legacy groups
      const defaultFees: YearWiseFee[] = [];
      const feePerYear = group.totalFee / group.durationYears;
      
      for (let i = 1; i <= group.durationYears; i++) {
        defaultFees.push({
          yearNumber: i,
          feeAmount: Math.round(feePerYear)
        });
      }
      setEditingYearWiseFees(defaultFees);
    }
    
    editForm.reset({
      name: group.name,
      code: group.code,
      description: group.description || "",
    });
    setIsEditDialogOpen(true);
  };

  // Update group handler
  const handleUpdateGroup = async (data: UpdateGroupWithFeesFormData) => {
    if (isSubmitting || !editingGroup) return;

    try {
      setIsSubmitting(true);
      
      // Check if year-wise fees have been modified
      const hasYearWiseFeesChanges = editingYearWiseFees.length > 0;
      
      if (hasYearWiseFeesChanges && canEditFees) {
        // Use the year-wise fees update endpoint
        const updateData: UpdateCourseGroupWithYearwiseFeesData = {
          name: data.name,
          code: data.code,
          description: data.description,
          yearWiseFees: editingYearWiseFees
        };
        
        await courseGroupService.updateCourseGroupWithYearwiseFees(editingGroup.id, updateData);
      } else {
        // Use the basic update endpoint (name, code, description only)
        const updateData: UpdateCourseGroupData = {
          name: data.name,
          code: data.code,
          description: data.description,
        };
        
        await courseGroupService.updateCourseGroup(editingGroup.id, updateData);
      }

      // Close dialog and reload
      setIsEditDialogOpen(false);
      setEditingGroup(null);
      setEditingYearWiseFees([]);
      await loadGroups();
    } catch (error) {
      console.error("Error updating group:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete group handler
  const handleDeleteGroup = async (group: CourseGroup) => {
    try {
      await courseGroupService.deleteCourseGroup(group.id);
      await loadGroups();
    } catch (error) {
      console.error("Error deleting group:", error);
    }
  };

  // Delete selected groups
  const handleDeleteSelected = async () => {
    try {
      const selectedRows = table.getSelectedRowModel().rows;
      const selectedGroups = selectedRows.map((row) => row.original);

      for (const group of selectedGroups) {
        await courseGroupService.deleteCourseGroup(group.id);
      }

      setRowSelection({});
      await loadGroups();
    } catch (error) {
      console.error("Error deleting selected groups:", error);
    }
  };

  // Search handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearchInProgress(true);
    setSearchTerm(value);
    setSearchInProgress(false);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm("");
    setSearchInProgress(false);
  }, []);

  // Year-wise fee management
  const updateYearFee = (yearNumber: number, amount: number) => {
    setEditingYearWiseFees(prev =>
      prev.map(fee =>
        fee.yearNumber === yearNumber
          ? { ...fee, feeAmount: Math.max(0, amount) }
          : fee
      )
    );
  };

  const calculateTotalFee = () => {
    return editingYearWiseFees.reduce((sum, fee) => sum + fee.feeAmount, 0);
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString()}`;
  };

  return (
    <div className="space-y-4 max-w-full">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-gray-900">Groups</h1>
          <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
            {groups.length}
          </Badge>
        </div>

        <div className="flex items-center gap-3 w-full md:w-1/2">
          {/* Search Input */}
          <SearchInput
            id={id}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onSearchClear={handleSearchClear}
            searchInProgress={searchInProgress}
          />

          {/* Toggle columns visibility / Delete selected - Combined in single container */}
          <div className="flex items-center border rounded-md bg-white">
            {/* Column Toggle Icon */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    hasFullAccess &&
                      table.getSelectedRowModel().rows.length > 0 &&
                      "rounded-r-none border-r"
                  )}
                >
                  <Columns3
                    className="opacity-60 h-4 w-4"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" className="w-56 p-3">
                <div className="space-y-3">
                  <div className="text-xs font-medium text-muted-foreground">
                    Toggle columns
                  </div>
                  <div className="space-y-4">
                    {table
                      .getAllColumns()
                      .filter((column) => column.getCanHide())
                      .map((column) => (
                        <div
                          key={column.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            checked={column.getIsVisible()}
                            onCheckedChange={(checked: boolean) =>
                              column.toggleVisibility(!!checked)
                            }
                          />
                          <Label className="flex grow justify-between gap-2 font-normal capitalize">
                            {column.id === "name"
                              ? "Group Details"
                              : column.id === "durationYears"
                              ? "Program Info"
                              : column.id === "isActive"
                              ? "Status"
                              : column.id === "totalFee"
                              ? "Fee Structure"
                              : column.id === "studentCount"
                              ? "Students"
                              : column.id}
                          </Label>
                        </div>
                      ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Delete Icon - Only show when rows are selected AND user has full access */}
            {hasFullAccess && table.getSelectedRowModel().rows.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative rounded-l-none text-red-500 hover:text-red-500"
                  >
                    <Trash2 className="stroke-2 h-4 w-4" aria-hidden="true" />
                    <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs bg-red-500 hover:bg-red-500">
                      {table.getSelectedRowModel().rows.length}
                    </Badge>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete{" "}
                      {table.getSelectedRowModel().rows.length} selected{" "}
                      {table.getSelectedRowModel().rows.length === 1
                        ? "group"
                        : "groups"}
                      .
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteSelected}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Add Group Button - Only show for users with full access */}
          {hasFullAccess && (
            <CreateCourseGroupWrapper onCourseGroupCreated={loadGroups} />
          )}
        </div>
      </div>

      {/* Groups Table */}
      <div className="rounded-lg border border-border bg-background">
        <div className="overflow-x-auto">
          <Table className="w-full">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-11">
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <div
                          className={cn(
                            header.column.getCanSort() &&
                              "flex h-full cursor-pointer select-none items-center justify-between gap-2"
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          onKeyDown={(e) => {
                            if (
                              header.column.getCanSort() &&
                              (e.key === "Enter" || e.key === " ")
                            ) {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e);
                            }
                          }}
                          tabIndex={header.column.getCanSort() ? 0 : undefined}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <ChevronUp
                                className="shrink-0 opacity-60"
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            ),
                            desc: (
                              <ChevronDown
                                className="shrink-0 opacity-60"
                                size={16}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                            ),
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      ) : (
                        <div className="flex h-full items-center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-gray-50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-4 py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columnsWithHandlers.length}
                    className="h-24 text-center"
                  >
                    {isLoading ? (
                      <Loader variant="loading-dots" text="Loading" />
                    ) : searchTerm ? (
                      `No groups found matching ${searchTerm}`
                    ) : (
                      "No course groups found."
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Group Dialog - Only show for users with full access */}
      {hasFullAccess && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Edit Course Group</DialogTitle>
              <DialogDescription>
                Update group information. Fee structure cannot be changed after creation.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto">
              <Form {...editForm}>
                <form
                  onSubmit={editForm.handleSubmit(handleUpdateGroup)}
                  className="space-y-6"
                >
                  {/* Editable Fields */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
                      Basic Information (Editable)
                    </h3>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <FormField
                        control={editForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Group Name *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., Computer Science Engineering"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={editForm.control}
                        name="code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Group Code *</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., CSE"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(e.target.value.toUpperCase())
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={editForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Brief description of the program"
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Read-only Program Structure */}
                  {editingGroup && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
                        Program Structure (Read-only)
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Duration</label>
                          <div className="text-sm font-semibold text-gray-900">
                            {editingGroup.durationYears} Years
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Students Enrolled</label>
                          <div className="text-sm font-semibold text-gray-900">
                            {editingGroup.studentCount} Students
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Editable Fee Structure */}
                  {editingGroup && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-900 border-b pb-2 flex items-center gap-2">
                        <IndianRupee className="h-4 w-4" />
                        Year-wise Fee Structure (Editable)
                      </h3>
                      
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-2 mb-4">
                          <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium">Fee Structure Updates</p>
                            <p>
                              Changes will only affect new students enrolled after this update. 
                              Existing students retain their locked fee structure from enrollment time.
                            </p>
                          </div>
                        </div>
                      </div>

                      {canEditFees ? (
                        /* Editable Year-wise Fees */
                        <div className="space-y-4">
                          {editingYearWiseFees.map((fee, index) => (
                            <div
                              key={fee.yearNumber}
                              className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border"
                            >
                              <Badge variant="outline" className="shrink-0">
                                Year {fee.yearNumber}
                              </Badge>
                              
                              <div className="flex-1">
                                <Input
                                  type="number"
                                  min={0}
                                  step={100}
                                  value={fee.feeAmount || ""}
                                  onChange={(e) => updateYearFee(fee.yearNumber, parseFloat(e.target.value) || 0)}
                                  placeholder="Enter fee amount"
                                  className="text-right"
                                />
                              </div>
                              
                              <div className="text-sm font-medium text-green-600 min-w-[100px] text-right">
                                {fee.feeAmount > 0 ? formatCurrency(fee.feeAmount) : "₹0"}
                              </div>
                            </div>
                          ))}
                          
                          {/* Total Fee Display */}
                          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calculator className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-800">
                                  Total Program Fee:
                                </span>
                              </div>
                              <span className="text-xl font-bold text-green-900">
                                {formatCurrency(calculateTotalFee())}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Read-only Fee Structure (fallback) */
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge variant="outline" className="bg-gray-100 text-gray-800">
                              Read-only Fee Structure
                            </Badge>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg border">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-600">
                                Total Program Fee:
                              </span>
                              <span className="text-lg font-semibold text-gray-900">
                                {editingGroup.formattedFee}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Fee editing is disabled for this course group.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </Form>
            </div>

            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingGroup(null);
                  setEditingYearWiseFees([]);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                onClick={editForm.handleSubmit(handleUpdateGroup)}
              >
                {isSubmitting ? "Updating..." : "Update Group"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CollegeGroups;
